import { calculateNutrientLimits } from '@/liver/limits'
import { entryHasAlcohol, fluidEntryHasNutrients } from '@/lib/alcohol'
import type {
  DiaryEntry,
  DailyLimits,
  DailySummary,
  HealthProfile,
  MealTime,
} from '@/types'

/**
 * Hydration GOAL default (spec §3): 2000ml to aim for — fluid is never a
 * restriction in the liver model. No per-condition fluid caps in v1
 * (decompensated users follow their care team's guidance instead).
 */
export const DEFAULT_FLUID_GOAL_ML = 2000

/**
 * Cirrhosis (both stages) and alcohol-related liver disease flip protein from
 * an informational value to a TARGET (1.2–1.5 g/kg, sarcopenia prevention).
 * Protein is never a ceiling in the liver model.
 */
function isProteinTargetCondition(condition?: string): boolean {
  return (
    condition === 'cirrhosis' ||
    condition === 'cirrhosis_decompensated' ||
    condition === 'alcohol_related'
  )
}

function safeWeightKg(weight?: number): number {
  if (typeof weight !== 'number' || !Number.isFinite(weight) || weight <= 20 || weight > 300) return 70
  return weight
}

/** Doctor-set override → stored calculatedLimits → recomputed from condition (mobile parity). */
export function resolveLimits(profile?: HealthProfile): DailyLimits | null {
  if (!profile) return null
  // Mobile always has healthProfile.calculatedLimits (set at onboarding). If it's
  // missing — or is a stale pre-liver shape — recompute from the condition.
  const stored = profile.calculatedLimits
  const calc =
    stored && typeof stored.calories === 'number'
      ? stored
      : calculateNutrientLimits(
          profile.condition || 'not_sure_prevention',
          profile.comorbidities,
          profile.weight,
        )
  const weightKg = safeWeightKg(profile.weight)
  const opt = profile.optionalNutrients

  const limits: DailyLimits = {
    calories: profile.dailyCalorieLimit ?? calc.calories,
    addedSugarsG: profile.dailyAddedSugarLimit ?? calc.addedSugarG,
    saturatedFatG: profile.dailySaturatedFatLimit ?? calc.saturatedFatG,
    fiberG: profile.dailyFiberTarget ?? calc.fiberG,
    sodiumMg: profile.dailySodiumLimit ?? calc.sodiumMg,
    proteinG: profile.dailyProteinTarget ?? Math.round(calc.proteinPerKg * weightKg),
    proteinIsTarget: calc.proteinIsTarget ?? isProteinTargetCondition(profile.condition),
    fluidMl: profile.dailyFluidGoal ?? DEFAULT_FLUID_GOAL_ML,
  }

  if (opt?.dailyCarbsLimit) limits.carbsG = opt.dailyCarbsLimit
  if (opt?.dailyFatLimit) limits.fatG = opt.dailyFatLimit
  if (opt?.dailyCholesterolLimit) limits.cholesterolMg = opt.dailyCholesterolLimit

  return limits
}

const MEAL_TYPES: MealTime[] = ['breakfast', 'lunch', 'dinner', 'snack']

// Ceilings only — fiber, protein and fluid are FLOORS/goals and must never
// flag a day "over limit" (the over-limit warning would punish healthy behavior).
const CEILING_KEYS = ['calories', 'addedSugars', 'saturatedFat', 'sodium', 'carbs', 'fat', 'cholesterol']

/** Pure summary computation — mirrors DiaryContext's useMemo. */
export function computeSummary(
  date: string,
  entries: DiaryEntry[],
  profile?: HealthProfile,
): DailySummary | null {
  const limits = resolveLimits(profile)
  if (!limits) return null

  const totals = {
    calories: 0,
    addedSugarsG: 0,
    sugarsG: 0,
    carbsG: 0,
    fiberG: 0,
    fatG: 0,
    saturatedFatG: 0,
    transFatG: 0,
    sodiumMg: 0,
    proteinG: 0,
    cholesterolMg: 0,
    fluidMl: 0,
  }
  const mealCounts: Record<MealTime, number> = {
    breakfast: 0,
    lunch: 0,
    dinner: 0,
    snack: 0,
  }

  for (const e of entries) {
    const n = e.nutrients || ({} as DiaryEntry['nutrients'])
    totals.calories += n.calories || 0
    totals.addedSugarsG += n.addedSugarsG || 0
    totals.sugarsG += n.sugarsG || 0
    totals.carbsG += n.carbsG || 0
    totals.fiberG += n.fiberG || 0
    totals.fatG += n.fatG || 0
    totals.saturatedFatG += n.saturatedFatG || 0
    totals.transFatG += n.transFatG || 0
    totals.sodiumMg += n.sodiumMg || 0
    totals.proteinG += n.proteinG || 0
    totals.cholesterolMg += n.cholesterolMg || 0
    // Alcohol is never hydration — a beer's volume doesn't count toward the
    // water goal (mobile DiaryContext parity).
    if (!entryHasAlcohol(e)) totals.fluidMl += e.fluidMl || 0

    // Mobile utils/fluids.ts thresholds: trace-content fluids (black coffee,
    // tea) stay tracker-only; nutritive fluids (milk, juice, soup) count as
    // meal-section entries.
    const isPureFluid = e.isFluid && !fluidEntryHasNutrients(e)
    if (!isPureFluid && MEAL_TYPES.includes(e.mealType)) {
      mealCounts[e.mealType] += 1
    }
  }

  const pctOf = (val: number, lim?: number) =>
    lim && lim > 0 ? (val / lim) * 100 : undefined

  const percentages: Record<string, number | undefined> = {
    calories: pctOf(totals.calories, limits.calories),
    addedSugars: pctOf(totals.addedSugarsG, limits.addedSugarsG),
    saturatedFat: pctOf(totals.saturatedFatG, limits.saturatedFatG),
    fiber: pctOf(totals.fiberG, limits.fiberG),
    sodium: pctOf(totals.sodiumMg, limits.sodiumMg),
    protein: pctOf(totals.proteinG, limits.proteinG),
    fluid: pctOf(totals.fluidMl, limits.fluidMl),
    carbs: pctOf(totals.carbsG, limits.carbsG),
    fat: pctOf(totals.fatG, limits.fatG),
    cholesterol: pctOf(totals.cholesterolMg, limits.cholesterolMg),
  }

  // Counts core ceilings PLUS any optional ceiling the user has set a limit
  // for (the over-limit warning; mobile computeDailySummary). A day's dot is
  // its day score band instead (diary/day-band.ts).
  const overLimitNutrients: string[] = []
  for (const key of CEILING_KEYS) {
    const p = percentages[key]
    if (p != null && p > 100) overLimitNutrients.push(key)
  }

  return {
    date,
    totals,
    limits,
    percentages,
    mealCounts,
    hasOverLimit: overLimitNutrients.length > 0,
    overLimitNutrients,
  }
}
